import type { RawDriverIdleEngine, RawDriverIdleEngineEvent, RawVehicleIdleEngine, RawVehicleIdleEngineEvent, UUID } from '@cobliteam/common-types';
import camelcaseKeys from 'camelcase-keys';
import moment from 'moment';

import type { Configuration, IRequest } from '../..';
import HttpMethod from '../../request/HttpMethod';
import Requester from '../../request/Requester';
import TimeZone from '../../request/Timezone';

enum IdleEngineAggregationTime {
  PERIODICAL = 'PERIODICAL',
  DAILY = 'DAILY',
}

export class IdleEngineRequester {
  private requester: Requester;

  private baseURL = '/analytics/v1/idle-engine';

  constructor(configuration: Configuration) {
    this.requester = new Requester(configuration);
  }

  public getVehicleIdleEngineAggregations = (startDate: number, endDate: number, idleEngineThresholdInMinutes?: number): Promise<RawVehicleIdleEngine[]> => {
    const requestBody = {
      start_date: moment(startDate).format(),
      end_date: moment(endDate).endOf('day').format(),
      timezone: TimeZone.getTimezone(),
      report_type: IdleEngineAggregationTime.PERIODICAL,
      idle_engine_threshold_in_minutes: idleEngineThresholdInMinutes,
    };

    const requestData: IRequest = {
      endpoint: `${this.baseURL}/vehicle`,
      method: HttpMethod.POST,
      body: JSON.stringify(requestBody),
    };

    return this.requester.request(requestData).then((response) => {
      const vehicleIdleEngineAggregations = response.data;
      return camelcaseKeys(vehicleIdleEngineAggregations, { deep: true });
    });
  };

  public getDriverIdleEngineAggregations = (startDate: number, endDate: number, idleEngineThresholdInMinutes?: number): Promise<RawDriverIdleEngine[]> => {
    const requestBody = {
      start_date: moment(startDate).format(),
      end_date: moment(endDate).endOf('day').format(),
      timezone: TimeZone.getTimezone(),
      report_type: IdleEngineAggregationTime.PERIODICAL,
      idle_engine_threshold_in_minutes: idleEngineThresholdInMinutes,
    };

    const requestData: IRequest = {
      endpoint: `${this.baseURL}/driver`,
      method: HttpMethod.POST,
      body: JSON.stringify(requestBody),
    };

    return this.requester.request(requestData).then((response) => {
      const driverIdleEngineAggregations = response.data;
      return camelcaseKeys(driverIdleEngineAggregations, { deep: true });
    });
  };

  public getVehicleIdleEngineEvents = (startDate: number, endDate: number, vehicleId: UUID, idleEngineThresholdInMinutes?: number): Promise<RawVehicleIdleEngineEvent[]> => {
    const requestBody = {
      start_date: moment(startDate).format(),
      end_date: moment(endDate).endOf('day').format(),
      timezone: TimeZone.getTimezone(),
      vehicle_id: vehicleId,
      idle_engine_threshold_in_minutes: idleEngineThresholdInMinutes,
    };

    const requestData: IRequest = {
      endpoint: `${this.baseURL}/vehicle/events`,
      method: HttpMethod.POST,
      body: JSON.stringify(requestBody),
    };

    return this.requester.request(requestData).then((response) => {
      const vehicleIdleEngineEvents = response.data;
      return camelcaseKeys(vehicleIdleEngineEvents, { deep: true });
    });
  };

  public getDriverIdleEngineEvents = (startDate: number, endDate: number, driverId: UUID, idleEngineThresholdInMinutes?: number): Promise<RawDriverIdleEngineEvent[]> => {
    const requestBody = {
      start_date: moment(startDate).format(),
      end_date: moment(endDate).endOf('day').format(),
      timezone: TimeZone.getTimezone(),
      driver_id: driverId,
      idle_engine_threshold_in_minutes: idleEngineThresholdInMinutes,
    };

    const requestData: IRequest = {
      endpoint: `${this.baseURL}/driver/events`,
      method: HttpMethod.POST,
      body: JSON.stringify(requestBody),
    };

    return this.requester.request(requestData).then((response) => {
      const driverIdleEngineEvents = response.data;
      return camelcaseKeys(driverIdleEngineEvents, { deep: true });
    });
  };
}

export default IdleEngineRequester;
